import ssf from '@elliemae/em-ssf-guest';
import { getAppConfigValue } from '@elliemae/pui-app-sdk';
import {
  EppsCustomField,
  NSSampleSchema,
  PricingSearchConfig,
  SchemaAndRules,
  getUpfrontPPEFeeValue,
  isGovtLoan,
  loanChannelConfiguration,
  updateToMostRecentSchemaVersion,
  mockLoanData,
  loanProperties as mockLoanProperties,
  originalLoanInformation as mockOriginalLoanInf,
  mapEppsCustomFields,
  AttributionCategory,
} from '@frontend/pricing-search';
import {
  openGlobalErrorModalAction,
  openNoLoanAmountErrorModal,
} from '../data/globalErrorModal/actionCreators';
import { setBuySideAdjustmentsAction } from '../data/origin/actionCreators';
import { setOriginalLoanDataAction } from '../data/origin/actions';
import { closeLoader, openLoader } from '../data/screenLoader/actions';
import {
  calculateGovtFeeAmount,
  isFirstMortgage,
} from '../view/SearchForm/components/LoanInformationForm/utils/helpers';
import {
  SET_LOANPROPERTIES,
  SET_LOCK_EXTENSION_DATA,
  SET_ORIGINALLOANINFORMATION,
  SET_SEARCH_CONFIGURATION,
  SET_SEARCH_CONFIGURATIONS,
  SET_USERDATA,
  UPDATE_LOANINFO,
  UPDATE_ORIGINAL_LOANINFO,
  UPDATE_ORIGINAL_LOAN_LOCKREQUEST,
  ATTRIBUTION_CATEGORIES,
} from './constants/ActionConstants';
import Common from './services/Common';
import Rights from './services/Rights';
import api from './utils/api';
import createAction from './utils/createAction';
import { mapCustomFieldsToSchema } from './utils/customFieldsHelpers';
import { isEmpty } from './utils/shared';
import Session, {
  CRED,
  IS_BUYSIDE,
  IS_SELLSIDE,
  IS_VALIDATE,
  OLD_ORIGIN_ID,
  ORIGIN_ID,
  ORIGIN_IS_UPDATE,
  ORIGIN_LOCKID,
  ORIGIN_SOURCE,
  ORIGIN_TRANSACTION_TYPE,
  PAT_TOKEN,
  USER_TYPE,
} from './services/Session';
import { RequestActionType, RequestLockStatus } from './constants/enumTypes';

export const setSearchConfiguration = (payload: SchemaAndRules) => ({
  // Placing this here following model of other actions on the root reducer
  // Would love to refactor all of it into a more organized system, at another time
  type: SET_SEARCH_CONFIGURATION,
  payload,
});

export const setSearchConfigurations = (payload: PricingSearchConfig[]) => ({
  type: SET_SEARCH_CONFIGURATIONS,
  payload,
});

// (ngen): imported from src/app/helpers.js

export const APPLICATION_ERROR = 'APPLICATION_ERROR';
export const IS_INVALID_TOKEN = 'INVALID_ACCESS_TOKEN';
export const IS_INVALID_CREDENTIAL = 'INVALID_CREDENTIAL';
export const IS_RECONCILE = 'RECONCILE_CREDENTIAL';
export const IS_RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND';
export const IS_INVALID_CREDS = 'EPPS2000';
export const IS_DISABLED_USER = 'EPPS2002';
export const IS_USER_CANNOT_BE_FOUND = 'EPPS40002';

const checkForHELOCInitialDraw = (loanData: any) => {
  const { loanInformation, loanType } = loanData;
  const { helocInitialDraw } = loanInformation;
  let hasHELOCInitialDraw = false;
  if (loanType === 'HELOC') {
    hasHELOCInitialDraw = helocInitialDraw !== null && helocInitialDraw >= 0;
  }
  return hasHELOCInitialDraw;
};

const checkForLoanAmount = ({
  lienPosition,
  firstMortgageAmount,
  secondMortgageAmount,
}) => {
  let hasLoanAmount = false;
  if (lienPosition === 1) {
    hasLoanAmount = !!firstMortgageAmount;
  }
  if (lienPosition === 2) {
    hasLoanAmount = !!secondMortgageAmount;
  }
  return hasLoanAmount;
};

export const parseLoanData = (loanData) => {
  let { secondMortgageAmount } = loanData.loanInformation;
  if (typeof secondMortgageAmount === 'number') {
    secondMortgageAmount = Math.trunc(secondMortgageAmount);
  }
  const result = {
    ...loanData,
    loanInformation: {
      ...loanData.loanInformation,
      secondMortgageAmount,
    },
  };
  return result;
};

export const updateLoanInfoSuccessAction = (data) => {
  const action = createAction(UPDATE_LOANINFO);
  const payload = parseLoanData(data);
  return action(payload);
};

export const setOriginalLoanInformation = createAction(
  SET_ORIGINALLOANINFORMATION,
);
export const setUserData = createAction(SET_USERDATA);
export const setLoanProperties = createAction(SET_LOANPROPERTIES);
export const setLockExtensionData = createAction(SET_LOCK_EXTENSION_DATA);
export const setAttributionCategories = createAction(ATTRIBUTION_CATEGORIES);
const updateOriginalLoanInfoAction = createAction(UPDATE_ORIGINAL_LOANINFO);
const updateOriginalLoanLockRequest = createAction(
  UPDATE_ORIGINAL_LOAN_LOCKREQUEST,
);
const showNoPermissionsError = (dispatch) =>
  dispatch(
    openGlobalErrorModalAction({
      title: 'You do not have permission to access this page.',
      errorMessage: 'Please contact your administrator to request changes.',
      onCloseCallback: () => Common.closeApplication(),
      confirmLabel: 'Close ICE PPE',
      showClose: false,
    }),
  );

export const getInitData = async (
  dispatch,
  isRefresh,
  setIsError,
  transactionType,
) => {
  dispatch(openLoader('Loading'));
  Session.set(USER_TYPE, { userType: 'LO' });
  ssf.connect();
  ssf.ready();
  await Common.sourceApplicationName();
  const data = await getOrigin(
    isRefresh,
    dispatch,
    setIsError,
    transactionType,
  );
  return data;
};

export const initiOriginData = async (
  transactionType,
  govtUpfrontFees,
  customFields,
  isZeroBasedPricing,
  dispatch,
  isRefresh,
  setIsError,
) => {
  const data = await getInitData(
    dispatch,
    isRefresh ?? false,
    setIsError ?? false,
    transactionType,
  );
  const categories = await api.getAttributionCategories();
  data.attributionCategories = categories?.attributionCategoryResponses;
  if (data) {
    const {
      notTokenCredResource,
      errorLoanAmountBlank,
      hasLoanAmount,
      loanAmountBlank,
      lienPosition,
      originData,
      attributionCategories,
    } = data;
    if (!notTokenCredResource) {
      if (errorLoanAmountBlank || !hasLoanAmount) {
        dispatch(openNoLoanAmountErrorModal() as any);
      }
      if (
        !loanAmountBlank &&
        lienPosition &&
        hasLoanAmount &&
        govtUpfrontFees.length > 0
      ) {
        dispatchOriginData(
          dispatch,
          originData,
          govtUpfrontFees,
          customFields,
          isZeroBasedPricing,
          attributionCategories,
        );
      }
    }
  }
};

export const dispatchUserData = async (dispatch) => {
  const userData = await Common.getUserData();
  if (userData && !Object.prototype.hasOwnProperty.call(userData, 'code')) {
    dispatch(setUserData(userData));
  }
};

export const setMockInitData = async (dispatch) => {
  const prvUser = Session.get(USER_TYPE);
  Session.set(USER_TYPE, { userType: !prvUser ? 'StandAlone' : prvUser });
  if (getAppConfigValue<boolean>('partner-ui.debugBuyside')) {
    Session.set(
      ORIGIN_TRANSACTION_TYPE,
      getAppConfigValue('partner-ui.dataSourceBuyside'),
    );
  }
  throw new Error('Setting mock data');
  dispatch(updateLoanInfoSuccessAction(mockLoanData));
  dispatch(setLoanProperties(mockLoanProperties));
  dispatch(setOriginalLoanInformation(mockOriginalLoanInf));
};

export const getSearchFormSchema = (
  loanData,
  searchConfiguration: PricingSearchConfig[],
  customFields,
) => {
  const loanChannelKey = loanData.loanInformation.loanChannel as
    | 0
    | 1
    | 2
    | 3
    | 4;
  if (loanChannelKey && searchConfiguration) {
    const loanChannel = loanChannelConfiguration[loanChannelKey];
    const channelSchema = searchConfiguration.find(
      (obj) => obj.channel === loanChannel,
    );
    if (channelSchema?.publishedSchema) {
      return channelSchema.publishedSchema;
    }
  }

  // If no channel schema is found, or no schema has been published, return the default schema with the custom field lookups
  if (customFields) {
    const mappedCustomFields = mapEppsCustomFields(
      customFields,
    ) as unknown as EppsCustomField[];
    if (mappedCustomFields && mappedCustomFields.length > 0) {
      const customSearchSchema = mapCustomFieldsToSchema(
        mappedCustomFields,
        NSSampleSchema,
      );
      return customSearchSchema;
    }
  }

  return NSSampleSchema;
};

export const dispatchOriginData = (
  dispatch,
  {
    originalLoanInformation,
    originalLoanInformation: { lockRequests, ltv },
    loanProperties,
    loanData,
    lockRequestInformation,
    extensionData,
    searchConfiguration,
  }: {
    originalLoanInformation: any;
    loanProperties: any;
    loanData: any;
    lockRequestInformation: any;
    extensionData: any;
    searchConfiguration: PricingSearchConfig[];
  },
  govtUpfrontFees: any[],
  customFields: EppsCustomField[],
  isZeroBasedPricing: boolean,
  attributionCategories: AttributionCategory[],
) => {
  const { credentials, rateLock } = originalLoanInformation;
  if (rateLock?.buySideLockDate) {
    loanData.loanInformation.lockDate = rateLock.buySideLockDate;
  }

  if (
    loanData.loanInformation.sectionOfAct === '184' &&
    loanData.standardProducts?.includes(2)
  ) {
    loanData.standardProducts = [];
    loanData.specialProducts = [136];
  }
  const { feeAmount, feeAmountFinanced, feeAmountPaidinCash, govtFeeFactor } =
    calculateGovtUpfrontAmount(
      loanData,
      originalLoanInformation,
      govtUpfrontFees,
      ltv,
    );
  if (feeAmount) {
    loanData.loanInformation.financedAmount = feeAmountFinanced || 0;
    loanData.loanInformation.feeAmount = feeAmount || 0;
    loanData.loanInformation.feeAmountFinanced = feeAmountFinanced || 0;
    loanData.loanInformation.feeAmountPaidinCash = feeAmountPaidinCash || 0;
    loanData.loanInformation.govtFeeFactor = govtFeeFactor || 0;
  }

  if (
    loanData.loanInformation.vaFundingFeeExempted &&
    loanData.standardProducts?.includes(3)
  ) {
    loanData.loanInformation.financedAmount = 0;
    loanData.loanInformation.feeAmount = 0;
    loanData.loanInformation.feeAmountFinanced = 0;
    loanData.loanInformation.feeAmountPaidinCash = 0;
    loanData.loanInformation.govtFeeFactor = 0;
  }

  loanData.loanInformation.target = convertPriceToParBased(
    isZeroBasedPricing,
    loanData.loanInformation.target,
    false,
    loanData,
  );
  loanData.isZeroBasedPricing = isZeroBasedPricing;
  // // Ensure backwards compatibility with older schemas in ICE PPE
  if (Array.isArray(searchConfiguration)) {
    searchConfiguration.forEach((searchConfiguration) => {
      if (searchConfiguration.draftSchema) {
        updateToMostRecentSchemaVersion(searchConfiguration.draftSchema);
      }
      if (searchConfiguration.publishedSchema) {
        updateToMostRecentSchemaVersion(searchConfiguration.publishedSchema);
      }
    });
  }

  dispatch(updateLoanInfoSuccessAction(loanData));
  dispatch(setLoanProperties(loanProperties));
  dispatch(setLockExtensionData(extensionData));
  dispatch(setOriginalLoanInformation(originalLoanInformation));
  dispatch(updateOriginalLoanInfoAction(loanData));
  dispatch(setOriginalLoanDataAction(originalLoanInformation));
  dispatch(updateOriginalLoanLockRequest(lockRequestInformation));
  dispatch(
    setBuySideAdjustmentsAction(
      originalLoanInformation?.rateLock?.buySideAdjustments || [],
    ),
  );
  dispatch(
    setSearchConfiguration(
      getSearchFormSchema(loanData, searchConfiguration, customFields),
    ),
  );
  dispatch(setSearchConfigurations(searchConfiguration));
  dispatch(setAttributionCategories(attributionCategories));
  Session.set(CRED, credentials);
  if (Session.get(IS_BUYSIDE)) {
    if (!Rights.accessBuyside && !Session.get(IS_VALIDATE)) {
      dispatch(closeLoader());
      showNoPermissionsError(dispatch);
      return;
    }
    Session.set(ORIGIN_IS_UPDATE, lockRequests || []);
  } else if (Session.get(IS_SELLSIDE)) {
    if (!Rights.accessSellside) {
      dispatch(closeLoader());
      showNoPermissionsError(dispatch);
      return;
    }
    Session.set(ORIGIN_IS_UPDATE, lockRequests || []);
  }
};

export const convertPriceToZeroBased = (target: number) => {
  if (Number.isFinite(target)) {
    return Number(-(target - 100).toFixed(3));
  }
  return target;
};
function getActionType(requestLockActionType: string) {
  const output = { isPriceAction: false, isRateAction: false };
  if (
    ['NewLock', 'ExpiredReLock', 'CancelledReLock'].includes(
      requestLockActionType,
    )
  ) {
    output.isPriceAction = true;
  } else if (
    ['ActiveNewLock', 'ActiveLockUpdate'].includes(requestLockActionType)
  ) {
    output.isRateAction = true;
  }
  return output;
}
export const convertPriceToParBased = (
  isZeroBasedPricing: boolean,
  target: number,
  convertForAPI: boolean,
  loanData: any,
) => {
  if (!isZeroBasedPricing) return target;
  const requestLockActionType = getRequestLockActionType(loanData);
  if (!requestLockActionType) return target;
  const { isPriceAction, isRateAction } = getActionType(requestLockActionType);
  const isHundredBased = checkTargetIsHundredBased(target);

  if (isPriceAction)
    return convertForAPI
      ? isHundredBased && !isZeroBasedPricing
        ? target
        : convertPriceToHundredBased(target)
      : isHundredBased && isZeroBasedPricing
      ? convertPriceToZeroBased(target)
      : 0;
  if (isRateAction)
    return convertForAPI
      ? convertPriceToHundredBased(target)
      : isHundredBased
      ? convertPriceToZeroBased(target)
      : target;

  return target;
};
export const getTargetLabelForZeroBasedPricing = (
  isZeroBasedPricing: boolean,
  targetLabel: string,
  loanData: any,
) => {
  if (!isZeroBasedPricing)
    return checkTargetIsHundredBased(loanData.loanInformation.target)
      ? 'Price'
      : 'Rate';
  const requestLockActionType = getRequestLockActionType(loanData);
  if (!requestLockActionType) return targetLabel;

  const { isPriceAction, isRateAction } = getActionType(requestLockActionType);
  if (isPriceAction) return 'Price';
  if (isRateAction) return 'Rate';
  return targetLabel;
};
export const getRequestLockActionType = (loanData) => {
  let type: string | null = null;
  if (!isEmpty(loanData)) {
    const { requestLockStatus, requestAction } = loanData;
    if (
      requestLockStatus === RequestLockStatus.NoStatus ||
      requestLockStatus === RequestLockStatus.NotLocked
    ) {
      if (
        requestAction === RequestActionType.QuoteRequest ||
        requestAction === RequestActionType.BuyRequest ||
        requestAction === RequestActionType.SellRequest
      ) {
        type = 'NewLock';
      }
    }
    if (requestLockStatus === RequestLockStatus.ActiveLock) {
      if (requestAction === RequestActionType.QuoteRequest)
        type = 'ActiveNewLock';
      if (requestAction === RequestActionType.ValidateReLock)
        type = 'ActiveLockUpdate';
    }
    if (requestLockStatus === RequestLockStatus.Expired) {
      if (requestAction === RequestActionType.ValidateReLock)
        type = 'ExpiredReLock';
    }
    if (requestLockStatus === RequestLockStatus.Cancelled) {
      if (requestAction === RequestActionType.ValidateReLock)
        type = 'CancelledReLock';
    }
  }
  return type;
};
export const convertPriceToHundredBased = (target: number) => {
  if (Number.isFinite(target)) {
    return 100 - target;
  }
  return target;
};
export const checkTargetIsHundredBased = (target: number) => {
  if (target >= -20 && target <= 20) {
    return false;
  }
  return true;
};

/* calculate government upfront fees for loanQualifier */
export const calculateGovtUpfrontAmount = (
  loanData,
  originalLoanInformation,
  govtUpfrontFees,
  ltv,
) => {
  let feeAmount = 0;
  let feeAmountFinanced = 0;
  let feeAmountPaidinCash = 0;
  let govtFeeFactor = 0;
  const { standardProducts, loanInformation, specialProducts } = loanData;
  const {
    loanPurpose,
    vaFirstTimeUse,
    lienPosition,
    firstMortgageAmount,
    secondMortgageAmount,
  } = loanInformation;

  if (isGovtLoan(standardProducts, specialProducts)) {
    const ppeGovtFeeFactor = getUpfrontPPEFeeValue(
      { standardProducts, specialProducts },
      { govtUpfrontFees, loanPurpose, vaFirstTimeUse, ltv },
    );
    let recursiveGovtFeeFactor = loanData.loanInformation
      .usdaGuarantee as number;
    if (
      standardProducts?.includes(4) &&
      loanData.loanInformation.usdaGuarantee > 0
    ) {
      // if Is USDA loan and an ENC factor has been applied,
      // assume that ENC has already found if we are financing full or partial,
      // and has applied the recursive values to the fee amount financed.
      // In this case, we will work backwards and find the factor based on the fee amount financed.
      recursiveGovtFeeFactor = loanData.loanInformation.usdaGuarantee;
    }
    const encGovtFeeFactor =
      standardProducts?.includes(4) &&
      loanData.loanInformation.usdaGuarantee > 0
        ? recursiveGovtFeeFactor
        : originalLoanInformation?.mortgageInsurancePremiumUpfrontFactorPercent;
    govtFeeFactor =
      encGovtFeeFactor && encGovtFeeFactor !== 0
        ? encGovtFeeFactor
        : ppeGovtFeeFactor;

    const baseLoanAmount = isFirstMortgage({
      customDependencies: { lienPosition },
    })
      ? firstMortgageAmount
      : secondMortgageAmount;
    feeAmount = calculateGovtFeeAmount(baseLoanAmount, govtFeeFactor);
    // FHA, VA, and HUD 184 calculations
    if (
      standardProducts?.includes(2) ||
      standardProducts?.includes(3) ||
      specialProducts?.includes(136)
    ) {
      // Has ENC Fee
      if (Number.isFinite(originalLoanInformation.rateLock?.fundingAmount)) {
        feeAmountPaidinCash =
          originalLoanInformation.rateLock.mipPaidInCash ?? 0;
        feeAmountFinanced =
          originalLoanInformation.rateLock.fundingAmount - feeAmountPaidinCash;
      }
      // No ENC Fee
      else {
        feeAmountFinanced = feeAmount;
      }
    }
    // USDA calculations
    if (standardProducts?.includes(4)) {
      feeAmountFinanced = loanData.loanInformation.feeAmountFinanced;
      feeAmountPaidinCash = loanData.loanInformation.feeAmountPaidinCash;
      feeAmount = loanData.loanInformation.feeAmount;
    }
  }
  // Prevent difference between cent amounts for initial pricing and qualification results
  const feeAmountHasCents = feeAmountFinanced % 1 > 0;
  const existingPricingAllMatchesAndDoesNotNeedChanged =
    !feeAmountHasCents &&
    feeAmount &&
    (feeAmountFinanced ?? 0) + (feeAmountPaidinCash ?? 0) === feeAmount;

  if (!existingPricingAllMatchesAndDoesNotNeedChanged) {
    const centsFinanced = Number(Number(feeAmountFinanced % 1).toFixed(2));
    if (centsFinanced) {
      feeAmountFinanced = Math.floor(feeAmountFinanced);
      feeAmountPaidinCash += centsFinanced;
    }
  }
  return { feeAmount, feeAmountFinanced, feeAmountPaidinCash, govtFeeFactor };
};

export const prepareLoanDocTypes = (loan: any, documentationTypes: any[]) => {
  // Populate null docType fields from documentationTypes lookup
  const documentationLevel = loan.documentationLevel;
  if (documentationLevel && documentationTypes.length > 0) {
    const docType = documentationTypes.find(
      (doc: any) => doc.documentationId === Number(documentationLevel),
    );
    if (docType) {
      if (
        loan.borrowerFinancialHistory.docTypeEmployment === null ||
        loan.borrowerFinancialHistory.docTypeEmployment === ''
      ) {
        const employmentType = docType.employment;
        if (employmentType === 100) {
          loan.borrowerFinancialHistory.docTypeEmployment = loan.loanInformation
            .selfEmployedBorrower
            ? '2'
            : '1';
        } else {
          loan.borrowerFinancialHistory.docTypeEmployment =
            employmentType?.toString();
        }
      }
      if (
        loan.borrowerFinancialHistory.docTypeAsset === null ||
        loan.borrowerFinancialHistory.docTypeAsset === ''
      ) {
        loan.borrowerFinancialHistory.docTypeAsset = docType.assets?.toString();
      }
      if (
        loan.borrowerFinancialHistory.docTypeIncome === null ||
        loan.borrowerFinancialHistory.docTypeIncome === ''
      ) {
        loan.borrowerFinancialHistory.docTypeIncome =
          docType.income?.toString();
      }
    }
  }

  // Pricing only supports 0 / 100 values for employment
  const docTypeEmployment =
    loan.borrowerFinancialHistory.docTypeEmployment?.toString();
  if (docTypeEmployment) {
    const isSelfEmployed = ['2', '200'].includes(docTypeEmployment);
    const isEmployed = ['1', '100', '2', '200'].includes(docTypeEmployment);
    loan.borrowerFinancialHistory.docTypeEmployment = isEmployed ? '100' : '0';
    loan.loanInformation.selfEmployedBorrower = isSelfEmployed;
  }

  // Populate NonQMDocLevel field when absent and NonQM product is selected
  if (
    loan.borrowerFinancialHistory?.nonQMDocLevel == null &&
    loan.standardProducts?.includes(6)
  ) {
    const docTypeIncome = loan.borrowerFinancialHistory.docTypeIncome;
    if (docTypeIncome === '100') {
      loan.borrowerFinancialHistory.nonQMDocLevel = '1';
    } else if (docTypeIncome === '70') {
      loan.borrowerFinancialHistory.nonQMDocLevel = '3';
    }
  }
};

export const validateOrigin = async (originData) => {
  const { loanData, code, summary } = originData;
  const { loanInformation } = loanData || {};
  const { lienPosition } = loanInformation || {};
  let isHELOCLoan;
  let hasLoanAmount;
  let hasHELOCInitialDraw;
  const notTokenCredResource = [
    IS_INVALID_TOKEN,
    IS_INVALID_CREDENTIAL,
    IS_RESOURCE_NOT_FOUND,
    IS_INVALID_CREDS,
    IS_DISABLED_USER,
    IS_USER_CANNOT_BE_FOUND,
  ].includes(code);

  if (loanData) {
    isHELOCLoan = loanData?.loanType === 'HELOC';
    hasHELOCInitialDraw = checkForHELOCInitialDraw(loanData || {});
    hasLoanAmount = checkForLoanAmount(loanInformation || {});
  }
  const loanAmountBlank =
    code === APPLICATION_ERROR &&
    summary ===
      'Loan Amount is Blank. Please enter Loan Amount and retry pricing';
  const validation: any = {
    notTokenCredResource,
    loanAmountBlank,
    hasLoanAmount,
    lienPosition,
    originData,
    code,
    isHELOCLoan,
    hasHELOCInitialDraw,
  };
  if (notTokenCredResource) {
    const reconcile = await Common.reconcileInvalidCredentials();
    if (
      reconcile.code === IS_INVALID_TOKEN ||
      reconcile.code === IS_INVALID_CREDENTIAL ||
      reconcile.code === IS_RESOURCE_NOT_FOUND ||
      reconcile.code === IS_INVALID_CREDS ||
      reconcile.code === IS_DISABLED_USER ||
      reconcile.code === IS_USER_CANNOT_BE_FOUND ||
      reconcile.token
    ) {
      validation[IS_RECONCILE] = IS_RECONCILE;
    }
    if (!reconcile) {
      validation.isRedirectToError = true;
    }
  }
  return validation;
};

const getTransactionData = async (isRefresh) => {
  const transactionObj = await ssf.getObject('transaction');
  const data = await (isRefresh
    ? transactionObj.refreshOrigin()
    : transactionObj.getOrigin());
  return data;
};

const setInfoSession = async (
  { source, partnerAccessToken, id, settings },
  dispatch,
  transactionType,
) => {
  let newSource = source;

  // TODO: temporary code. This should be replace when reviselock story is going to implement
  if (source) {
    newSource = source.replace('reviselock', 'getbuysidepricing');
  }

  if (getAppConfigValue<boolean>('partner-ui.debugBuyside')) {
    newSource = getAppConfigValue('partner-ui.dataSourceBuyside');
  }

  // overwrite transaction type came from ENCW as we switch between buyside/sellside from partner UI.
  if (transactionType) {
    if (transactionType === 'getsellsidepricing') {
      newSource = newSource.replace('getbuysidepricing', 'getsellsidepricing');
    } else if (transactionType === 'getbuysidepricing') {
      newSource = newSource.replace('getsellsidepricing', 'getbuysidepricing');
    }
  }

  Session.set({
    [PAT_TOKEN]: partnerAccessToken,
    [ORIGIN_SOURCE]: newSource,
    [ORIGIN_TRANSACTION_TYPE]: newSource,
    [ORIGIN_LOCKID]: newSource,
    [OLD_ORIGIN_ID]: Session.get(ORIGIN_ID),
    [ORIGIN_ID]: id,
  });
};

const openDocumentViewer = async (transactionId) => {
  const response = await api.getTransactionDetails(transactionId);
  if (response && response.resources) {
    try {
      const applicationObject = await ssf.getObject('application');
      const modalStatus = await applicationObject.openModal({
        target: {
          entityId: response.resources.id,
          entityType: 'urn:elli:skydrive',
        },
      });
      if (modalStatus === 'success') {
        await Common.closeApplication();
      }
    } catch (error) {
      // console.log({ error });
    }
  }
};

export const getOrigin = async (
  isRefresh,
  dispatch,
  setIsError,
  transactionType,
) => {
  const redirectToError = () => {
    setIsError(true);
  };
  const data = await getTransactionData(isRefresh);
  setInfoSession(data, dispatch, transactionType);
  const { transactionId } = data;
  if (transactionId) {
    await openDocumentViewer(transactionId);
  }
  if (!transactionId) {
    try {
      const originData = await Common.getOrigin();
      const result = validateOrigin(originData);

      if (transactionType === 'getsellsidepricing') {
        sessionStorage.setItem(ORIGIN_TRANSACTION_TYPE, transactionType);
      }

      return await result;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      dispatch(closeLoader());
      dispatch(
        openGlobalErrorModalAction({
          errorMessage:
            'Something went wrong getting Origin. Please contact the administrator.',
          onCloseCallback: () => {
            redirectToError();
          },
        }),
      );
      redirectToError();
    }
  }
  return null;
};
